import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import GreyBox from '~/common/components/GreyBox';
import SubHeader from '~/common/components/Subheader';


// Import Local Styles
import styles from './plan-details.css';

const PlanDetails = (props) => {
    const { modifier, planDetails, showDiscount, digitalListingAddon } = props;

    if (!planDetails) return false;
    const { term, totalAmount, name, offerAmount, taxesAndSurcharges, chargeEstimates, discountAmount } = planDetails;

    const isAnnual = term && term.toLowerCase() === 'annual';

    const rubyDetail = chargeEstimates.find(addOn =>
        addOn.amount > 0 && addOn.chargeType === 'Feature');


    const annualTotal = useMemo(() => {
        if (isAnnual) {
            const total = rubyDetail ? totalAmount + (rubyDetail.amount * 11) : totalAmount;
            return digitalListingAddon ? (total + digitalListingAddon.amount).toFixed(2) : total.toFixed(2);
        }
        return digitalListingAddon ? (((totalAmount - digitalListingAddon.amount) * 12) + digitalListingAddon.amount).toFixed(2) : (totalAmount * 12).toFixed(2);
    }, []);

    // Format Discount amount to show as `$0.00`
    const discountFormat = () => {
        const discount = `${discountAmount}`;
        // If discount amount has decimals, then return it
        if (discount.includes('.')) {
            return `-$${discount}`;
        }
        // If discount is 0, no need to show `-` infront.
        return discountAmount ? `-$${discount}.00` : `$${discount}.00`;
    };

    // remove all add ons that that are not active (value 0) or the offer and minutes in offer since those are in the offerAmount
    const filteredChargeEstimates = chargeEstimates.filter((addOn) => {
        return addOn.amount > 0 && addOn.chargeType !== 'MinutesInOffer' && addOn.chargeType !== 'Offer' && addOn.chargeType !== 'Surcharge';
    });


    const listItem = (detail, amount, key, chargeType) => {
        const isDigitalListingAddon = detail === digitalListingAddon.name;
        const timesCopy = isAnnual && !isDigitalListingAddon && <span className={styles.small}>12 x</span>;
        const total = isAnnual && !isDigitalListingAddon && chargeType !== 'Feature' ? amount / 12 : amount;
        let fullDetails = isAnnual && chargeType === 'Feature' ? <span>{detail}<br />(Billed Monthly)</span> : detail;
        if (isDigitalListingAddon) {
            fullDetails = <span>{detail}<br />(Billed Annually)</span>;
        }
        return <li className={styles.detail} key={key}>{fullDetails} <span className={styles.price}>{timesCopy} ${total.toFixed(2)}</span></li>;
    };

    return (
        <GreyBox modifier={modifier}>
            <SubHeader modifier={styles.subheader}>{term} Plan</SubHeader>
            <div className={styles.headline}>
                <h3>{term} Total</h3>
                <span className={styles.price}> {isAnnual && <span className={styles.small}>12 x</span>} ${(annualTotal / 12).toFixed(2)}</span>
            </div>
            <ul className={styles['detail-list']}>
                {listItem(`${name} Plan`, offerAmount, offerAmount)}
                {listItem('Taxes & Surcharges', taxesAndSurcharges, taxesAndSurcharges)}
                {filteredChargeEstimates.map((addOn, i) => {
                    return listItem(addOn.name, addOn.amount, i, addOn.chargeType);
                })}
                {isAnnual && digitalListingAddon && listItem(digitalListingAddon.name, digitalListingAddon.amount)}
            </ul>

            {showDiscount && <div className={`${styles.headline} ${styles.rowSpace}`}>
                <h3>Discount</h3>
                <span className={`${styles.price} ${discountAmount ? styles.discount : ''}`}>{discountFormat()}</span>
            </div>}

            <div className={`${styles.headline} ${styles['headline--bold']} ${isAnnual && rubyDetail && styles.rowSpace}`}>
                <h3>Total</h3>
                <span className={styles.price}>${(annualTotal - discountAmount).toFixed(2)} /year</span>
            </div>

            {isAnnual && rubyDetail &&
                <div className={`${styles.headline} ${styles['headline--bold']}`}>
                    <h3>Due Now</h3>
                    <span className={styles.price}>${totalAmount}</span>
                </div>}
        </GreyBox>
    );
};

PlanDetails.propTypes = {
    modifier: PropTypes.string,
    planDetails: PropTypes.shape({
        term: PropTypes.string.isRequired,
        totalAmount: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        offerAmount: PropTypes.number.isRequired,
        taxesAndSurcharges: PropTypes.number.isRequired,
        chargeEstimates: PropTypes.object.isRequired
    }).isRequired
};

export default PlanDetails;
